import React, { useEffect, useRef } from 'react';
import anime from 'animejs';

const AnimatedLogo: React.FC = () => {
  const orbRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!orbRef.current || !containerRef.current) return;

    // Create multiple layers of neural connections for enhanced depth
    const createConnections = (count: number, className: string) => {
      return [...Array(count)].map(() => {
        const el = document.createElement('div');
        el.className = className;
        containerRef.current?.appendChild(el);
        return el;
      });
    };

    // Create three layers for more depth
    const backConnections = createConnections(20, 'neural-connection back');
    const midConnections = createConnections(24, 'neural-connection mid');
    const frontConnections = createConnections(16, 'neural-connection front');

    // Create floating particles with different sizes
    const createParticles = () => {
      return [...Array(24)].map(() => {
        const el = document.createElement('div');
        el.className = 'particle';
        const size = Math.random() * 3 + 2; // Random size between 2-5px
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        containerRef.current?.appendChild(el);
        return el;
      });
    };

    const particles = createParticles();

    // Create synaptic flashes
    const createSynapticFlashes = () => {
      return [...Array(8)].map(() => {
        const el = document.createElement('div');
        el.className = 'synaptic-flash';
        containerRef.current?.appendChild(el);
        return el;
      });
    };

    const flashes = createSynapticFlashes();

    const timeline = anime.timeline({
      loop: true,
    });

    // Enhanced orb animation
    timeline
      .add({
        targets: orbRef.current,
        scale: [0.95, 1.05],
        rotate: [0, 360],
        duration: 8000,
        easing: 'easeInOutSine',
        loop: true
      })
      // Back layer connections
      .add({
        targets: '.neural-connection.back',
        opacity: [0, 0.3, 0],
        scale: [0, 0.8],
        translateX: () => anime.random(-120, 120),
        translateY: () => anime.random(-120, 120),
        translateZ: -80,
        delay: anime.stagger(100),
        duration: 4000,
        easing: 'easeOutExpo',
        loop: true
      }, '-=8000')
      // Mid layer connections
      .add({
        targets: '.neural-connection.mid',
        opacity: [0, 0.5, 0],
        scale: [0, 0.9],
        translateX: () => anime.random(-100, 100),
        translateY: () => anime.random(-100, 100),
        translateZ: -40,
        delay: anime.stagger(80),
        duration: 3500,
        easing: 'easeOutExpo',
        loop: true
      }, '-=8000')
      // Front layer connections
      .add({
        targets: '.neural-connection.front',
        opacity: [0, 0.8, 0],
        scale: [0, 1],
        translateX: () => anime.random(-80, 80),
        translateY: () => anime.random(-80, 80),
        translateZ: 50,
        delay: anime.stagger(50),
        duration: 3000,
        easing: 'easeOutExpo',
        loop: true
      }, '-=8000')
      // Particles animation
      .add({
        targets: '.particle',
        opacity: [0, 0.8, 0],
        scale: [0.5, 1.5],
        translateX: () => anime.random(-150, 150),
        translateY: () => anime.random(-150, 150),
        translateZ: () => anime.random(-50, 50),
        rotate: () => anime.random(-180, 180),
        delay: anime.stagger(200),
        duration: 6000,
        easing: 'easeInOutQuad',
        loop: true
      }, '-=8000')
      // Synaptic flashes
      .add({
        targets: '.synaptic-flash',
        opacity: [0, 1, 0],
        scale: [0, 3],
        translateX: () => anime.random(-100, 100),
        translateY: () => anime.random(-100, 100),
        delay: anime.stagger(1000),
        duration: 2000,
        easing: 'easeOutExpo',
        loop: true
      }, '-=8000');

    return () => timeline.pause();
  }, []);

  return (
    <div className="relative w-full h-full perspective-1000" ref={containerRef}>
      {/* Enhanced background glow */}
      <div className="absolute inset-0 rounded-full bg-gradient-radial from-purple-600/30 via-blue-500/20 to-transparent blur-3xl transform scale-150 animate-pulse" />
      
      {/* Main orb with enhanced visual effects */}
      <div
        ref={orbRef}
        className="absolute inset-0 rounded-full shadow-[0_0_100px_rgba(147,51,234,0.6)] transform-gpu"
        style={{
          background: `radial-gradient(circle at 30% 30%, 
            rgba(59, 130, 246, 0.95) 0%, 
            rgba(124, 58, 237, 0.95) 50%,
            rgba(147, 51, 234, 0.95) 100%)`
        }}
      >
        {/* Enhanced glass effect */}
        <div className="absolute inset-0 rounded-full bg-[rgba(255,255,255,0.15)] backdrop-blur-sm" />
        
        {/* Enhanced glow effect */}
        <div className="absolute inset-0 rounded-full glow-effect" />
        
        {/* Enhanced inner core */}
        <div className="absolute inset-8 rounded-full core-glow" />
        
        {/* Energy rings */}
        <div className="absolute inset-4 rounded-full energy-ring" />
        <div className="absolute inset-12 rounded-full energy-ring delay-1000" />
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .perspective-1000 {
          perspective: 1000px;
        }

        .neural-connection {
          position: absolute;
          background: linear-gradient(to right, rgba(96, 165, 250, 0.9), rgba(139, 92, 246, 0.9));
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          opacity: 0;
        }

        .neural-connection.back {
          width: 2px;
          height: 2px;
          box-shadow: 0 0 15px rgba(147, 51, 234, 0.4);
          z-index: -1;
        }

        .neural-connection.mid {
          width: 3px;
          height: 3px;
          box-shadow: 0 0 20px rgba(147, 51, 234, 0.5);
          z-index: 0;
        }

        .neural-connection.front {
          width: 4px;
          height: 4px;
          box-shadow: 0 0 25px rgba(147, 51, 234, 0.7);
          z-index: 10;
        }

        .particle {
          position: absolute;
          background: radial-gradient(circle at center,
            rgba(255, 255, 255, 0.95),
            rgba(147, 51, 234, 0.7)
          );
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          box-shadow: 0 0 25px rgba(147, 51, 234, 0.8);
          opacity: 0;
        }

        .synaptic-flash {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.9);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          box-shadow: 
            0 0 30px rgba(147, 51, 234, 0.8),
            0 0 60px rgba(59, 130, 246, 0.6);
          opacity: 0;
        }

        .glow-effect {
          background: radial-gradient(circle at center,
            rgba(255, 255, 255, 0.3) 0%,
            rgba(255, 255, 255, 0.2) 20%,
            rgba(255, 255, 255, 0) 70%
          );
          animation: pulse 4s ease-in-out infinite alternate;
        }

        .core-glow {
          background: radial-gradient(circle at center,
            rgba(255, 255, 255, 0.5) 0%,
            rgba(147, 51, 234, 0.3) 50%,
            transparent 100%
          );
          animation: core-pulse 3s ease-in-out infinite alternate;
          filter: blur(8px);
        }

        .energy-ring {
          border: 2px solid rgba(255, 255, 255, 0.3);
          animation: energy-pulse 4s ease-in-out infinite;
        }

        .energy-ring.delay-1000 {
          animation-delay: 1s;
        }

        @keyframes pulse {
          0% {
            opacity: 0.3;
            transform: scale(0.95) rotate(0deg);
          }
          100% {
            opacity: 0.7;
            transform: scale(1.05) rotate(180deg);
          }
        }

        @keyframes core-pulse {
          0% {
            opacity: 0.4;
            transform: scale(0.8);
          }
          100% {
            opacity: 0.8;
            transform: scale(1.2);
          }
        }

        @keyframes energy-pulse {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
          100% {
            opacity: 0;
            transform: scale(1.4);
          }
        }
      `}} />
    </div>
  );
};

export default AnimatedLogo;